import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { API_GET, DBRequest, useConfig } from '../../../api/api';
import TypeSkeleton from './TypeSkeleton';
import FormHelperText from '../FormHelperText';
import { useTranslation } from 'react-i18next';
import LabeledBox from '../../special/LabeledBox';
import ExoDialog from '../../exo/ExoDialog';
import ExoAvatar from '../../exo/ExoAvatar';
import DBSearch from '../../special/DBSearch';
import CardGrid from '../../cards/layout/CardGrid';
import MediaPreview from '../../exo/explorer/MediaPreview';
import { tokens } from '../../../global/theme/tokens';
import DeleteIcon from '@mui/icons-material/Delete';
import Cookies from 'universal-cookie';

const TypeProject = ({
  updateValidationOnChange,
  dataCollection,
  errors,
  field,
  size,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const projectData = dataCollection?.[field.key];

    if (!projectData) {
      setSelected(null);
      return;
    }

    // Only update if the new value differs from the current selection
    setSelected((prevSelected) => {
      if (
        !prevSelected ||
        JSON.stringify(prevSelected) !== JSON.stringify(projectData)
      ) {
        return projectData;
      }
      return prevSelected;
    });
  }, [dataCollection, field.key]);

  function handleChangeSelect(data) {
    setSelected(data);
    updateValidationOnChange(field.key, data);
    setOpen(false);
  }
  const projectPicture =
    dataCollection[field.key] && dataCollection[field.key]?.image?.temporaryUrl;

  return (
    <Box
      sx={{
        gridColumn: field.colspan
          ? `span ${field.colspan} / span ${field.colspan}`
          : 'auto',
      }}
      className="flex w-full gap-2"
    >
      <LabeledBox
        className="w-full"
        label={field.label}
        onClick={() => setOpen(true)}
      >
        {selected && (
          <Box className="flex flex-wrap items-center gap-2 min-w-0">
            <Box className="flex items-center gap-1 min-w-0 truncate">
              <Box className="flex items-center gap-2 w-full">
                <ExoAvatar
                  size="26px"
                  name={
                    selected.clientType === 'company'
                      ? selected.client?.name
                      : `${selected.client?.firstName} ${selected.client?.lastName}`
                  }
                  picture={
                    projectPicture ||
                    (selected.client?.logo &&
                      selected.client?.logo.temporaryUrl)
                  }
                  type={selected.clientType}
                />
                <Typography>{selected.name}</Typography>
              </Box>
            </Box>
          </Box>
        )}
      </LabeledBox>

      {selected && (
        <Tooltip title={t('Clear')}>
          <Button
            onClick={() => {
              setSelected(null);
              updateValidationOnChange(field.key, null);
            }}
            sx={{
              padding: 0,
              width: size === 'small' ? '40px' : '53px',
              bgcolor: colors.bgInput,
              minWidth: '0px',
              border: '1px solid transparent',
              ':hover': {
                bgcolor: colors.error + '30',
                border: `1px solid ${colors.error}80`,
                color: colors.error,
              },
            }}
          >
            <DeleteIcon />
          </Button>
        </Tooltip>
      )}
      <FormHelperText />
      <ExoDialog open={open} onClose={() => setOpen(false)}>
        <SelectProject
          types={field.types}
          onSelect={handleChangeSelect}
          onClose={() => setOpen(false)}
          fieldUserId={field.userId}
        />
      </ExoDialog>
    </Box>
  );
};

export const SelectProject = ({ onSelect, onClose, types, fieldUserId }) => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  const cookies = new Cookies();
  const userId = cookies.get('userId');

  const [projectStatus, setProjectStatus] = useState('own');
  const [filter, setFilter] = useState('');

  const config = useConfig();

  //Get request for specific project
  const handleProjectSelect = (project) => {
    if (!config || !project?.id) return;

    DBRequest({
      config,
      path: `projects/${project.id}`,
      method: API_GET,
      onResponse: (projectData) => {
        const fullProject = projectData.data;
        setSelectedProject(fullProject);
        onSelect?.(fullProject); // Use the fetched full project directly
        onClose?.();
      },
    });
  };

  useEffect(() => {
    const newFilter =
      projectStatus === 'own'
        ? `status = 0 AND teamMemberIds = ${fieldUserId || userId}`
        : 'status = 0';

    setFilter(newFilter);
  }, [projectStatus]);

  return (
    <Box className="flex flex-col gap-4 overflow-y-auto h-full">
      <DBSearch param="projects" onSearch={setProjects} filter={filter}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">{t('Projects')}</Typography>
          <ToggleButtonGroup
            size="small"
            value={projectStatus}
            exclusive
            onChange={(e) => setProjectStatus(e.target.value)}
            aria-label="Project Status"
            className="backdrop-blur-lg mt-2"
          >
            <ToggleButton value="own">{t('Active')}</ToggleButton>
            <ToggleButton value="all">{t('All Projects')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <CardGrid>
          {projects ? (
            projects.map((project, index) => (
              <Project
                key={index}
                project={project}
                types={types}
                onSelect={handleProjectSelect}
                onClose={onClose}
              />
            ))
          ) : (
            <TypeSkeleton n={25} />
          )}
        </CardGrid>
      </DBSearch>
    </Box>
  );
};

//Projects list
const Project = ({ project, onSelect, types }) => {
  return (
    <Button
      onClick={() => onSelect(project)}
      variant="contained"
      color="secondary"
      sx={{
        transition: 'scale 0.3s ease',
      }}
      className=" cursor-pointer w-full flex flex-col backdrop-blur-lg items-center p-4 rounded-lg"
    >
      <ExoAvatar
        picture={project.image}
        type={'company'}
        name={project.subLabel}
        size="90px"
      />
      <Typography variant="h3" className="pt-4">
        <b>{project.nr}</b>
      </Typography>
      <Typography variant="h6">{project.label}</Typography>
    </Button>
  );
};

export default TypeProject;
